import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, finalize } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ItfgDateAdapter } from '@app/core/adapters/date-adapter.service';
import { HttpErrorResponse } from '@angular/common/http';
import moment from 'moment';
import { Client } from '@app/core/types/client';
import { Currency } from '@app/core/types/currency';
import {
  CreditLimitOffer,
  CreditLimitOfferDto,
} from '@app/core/types/credit-limit-offer';
import { CreditLimitOfferService } from '@app/core/services/credit-limit-offers.service';
import { NotificationService } from '@app/core/services/notification.service';
import { CurrencyService } from '@app/core/services/currency.service';

const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'itfg-credit-limit-offer-create-dialog',
  templateUrl: './credit-limit-offer-create-dialog.component.html',
  styleUrls: ['./credit-limit-offer-create-dialog.component.scss'],
  standalone: false,
  providers: [
    { provide: DateAdapter, useClass: ItfgDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class CreditLimitOfferCreateDialogComponent implements OnInit, OnDestroy {
  offerForm: FormGroup;
  isLoading: boolean = false;
  defaultCurrencySymbol: string;
  tomorrowDate: Date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  _unsubscribe = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private creditLimitOfferService: CreditLimitOfferService,
    private notificationService: NotificationService,
    private currencyService: CurrencyService,
    public dialogRef: MatDialogRef<CreditLimitOfferCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      client: Client;
    }
  ) {
    this.offerForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadDefaultCurrency();
  }

  createForm(): FormGroup {
    return this.fb.group({
      amount: [null, [Validators.required, Validators.min(1)]],
      validTo: [null, Validators.required],
    });
  }

  loadDefaultCurrency(): void {
    this.currencyService.defaultCurrency
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((defaultCurrency: Currency) => {
        this.defaultCurrencySymbol = defaultCurrency.symbol;
      });
  }

  onSubmit(): void {
    this.offerForm.markAllAsTouched();

    if (this.offerForm.invalid) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'news.messages.requiredFields',
      });
      return;
    }

    this.createOffer();
  }

  createOffer(): void {
    this.isLoading = true;
    const formValue = this.offerForm.value;

    const offerDto: CreditLimitOfferDto = {
      offer: {
        amount: formValue.amount,
        validTo: moment(formValue.validTo).endOf('day').toISOString(),
      },
      client: {
        userId: this.data.client.id,
      },
    };

    this.creditLimitOfferService
      .createCreditLimitOffer$(offerDto)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this._unsubscribe)
      )
      .subscribe({
        next: (createdOffer: CreditLimitOffer) => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'creditLimitOffers.offerCreatedSuccessfully',
          });
          this.dialogRef.close(createdOffer);
        },
        error: (error: HttpErrorResponse) => {
          this.handleCreateError(error);
        },
      });
  }

  handleCreateError(error: HttpErrorResponse): void {
    const errorCode =
      error.error?.code ||
      error.error?.[0]?.errorCode ||
      error.error?.[0]?.code;

    if (errorCode) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: `creditLimitOffers.errors.${errorCode.toLowerCase()}`,
      });
    } else {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'creditLimitOffers.createOfferErrorMessage',
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
